'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { useAI } from '@/context/AIContext'
import { cn } from '@/lib/utils'
import { ThreadListPane } from './ThreadListPane'
import { ChatStreamPane } from './ChatStreamPane'
import { WidgetCanvasPane } from './WidgetCanvasPane'

// ============================================================================
// Animation Variants
// ============================================================================

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
}

const containerVariants = {
  hidden: { 
    opacity: 0,
    scale: 0.99 
  },
  visible: { 
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.05,
      ease: 'easeOut',
    }
  },
  exit: { 
    opacity: 0,
    scale: 0.99,
    transition: {
      duration: 0.05,
    }
  },
}

// ============================================================================
// Split Screen Manager Component
// ============================================================================

export function SplitScreenManager() {
  const {
    isManagerOpen,
    closeManager,
    viewMode,
    rightPaneOpen,
  } = useAI()

  // Don't render if not open
  if (!isManagerOpen) return null

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeManager()
    }
  }

  // Determine layout based on view mode
  const isExpanded = viewMode === 'expanded'

  return (
    <AnimatePresence mode="wait">
      {isManagerOpen && (
        <>
          {/* Backdrop with blur */}
          <motion.div
            key="split-screen-backdrop"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.05 }}
            onClick={handleBackdropClick}
            className="fixed inset-0 z-modal-backdrop"
            style={{
              backgroundColor: 'rgba(26, 35, 64, 0.5)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
            aria-hidden="true"
          >
            {/* Container */}
            <motion.div
              key="split-screen-container"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'z-modal h-full flex',
                isExpanded 
                  ? 'w-full'  // Full screen for expanded mode
                  : 'mx-auto my-8 w-full max-w-2xl max-h-[80vh] rounded-2xl shadow-2xl'  // Modal for collapsed
              )}
            >
              {isExpanded ? (
                // Expanded Mode: Three-pane layout
                <div className="flex w-full h-full bg-white" onClick={(e) => e.stopPropagation()}>
                  {/* Left Pane: Thread List (25%) */}
                  <div className="w-1/4 border-r border-border/30" style={{ pointerEvents: 'auto' }}>
                    <ThreadListPane />
                  </div>
                  
                  {/* Middle Pane: Chat Stream (50% or 75% if right closed) */}
                  <div className={cn(
                    'transition-all duration-300',
                    rightPaneOpen ? 'w-1/2' : 'w-3/4'
                  )}>
                    <ChatStreamPane isExpanded={true} />
                  </div>
                  
                  {/* Right Pane: Widget Canvas (25%, collapsible) */}
                  <AnimatePresence>
                    {rightPaneOpen && (
                      <motion.div
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: '25%', opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-l border-border/30 overflow-hidden"
                      >
                        <WidgetCanvasPane />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                // Collapsed Mode: Simple modal (existing behavior)
                <div className="w-full h-full bg-white rounded-2xl flex flex-col overflow-hidden">
                  <ChatStreamPane isExpanded={false} />
                </div>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
